import { buildAuthenticatedHeaders } from './authStorage.ts';
import type { AuthLoginResponse, AuthSession, AuthUser } from './apiContract.ts';
export type { AuthLoginResponse, AuthSession, AuthUser } from './apiContract.ts';

export class AuthApiError extends Error {
  readonly kind: 'network' | 'http';
  readonly statusCode: number | null;

  constructor(message: string, kind: 'network' | 'http', statusCode: number | null = null) {
    super(message);
    this.name = 'AuthApiError';
    this.kind = kind;
    this.statusCode = statusCode;
  }
}

function getAuthApiBaseUrl(): string {
  const raw = String(import.meta.env.VITE_DEMO_API_BASE_URL || '/api').trim();
  if (!raw) return '/api';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

async function requestAuthJson<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${getAuthApiBaseUrl()}${path}`, {
      ...init,
      credentials: 'include',
      headers: buildAuthenticatedHeaders({
        Accept: 'application/json',
        ...(init?.headers || {}),
      }),
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Erreur réseau';
    throw new AuthApiError(detail, 'network');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    let detail = response.statusText;
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { detail?: string; error?: string };
      detail = payload.detail || payload.error || detail;
    } else {
      detail = (await response.text()) || detail;
    }
    throw new AuthApiError(`HTTP ${response.status}: ${detail}`, 'http', response.status);
  }

  if (!contentType.includes('application/json')) {
    throw new AuthApiError(`Type de contenu inattendu renvoyé par l’API d’authentification : ${contentType || 'inconnu'}`, 'http', response.status);
  }

  return (await response.json()) as T;
}

export async function fetchAuthSession(signal?: AbortSignal): Promise<AuthSession> {
  return requestAuthJson<AuthSession>('/auth/session', {
    signal,
  });
}

export async function loginWithAuthIdentity(payload: { email?: string; userId?: string; password: string }): Promise<AuthLoginResponse> {
  return requestAuthJson<AuthLoginResponse>('/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function logoutAuthSession(): Promise<AuthSession> {
  return requestAuthJson<AuthSession>('/auth/logout', {
    method: 'POST',
  });
}

export async function fetchLoginUsers(signal?: AbortSignal): Promise<AuthUser[]> {
  return requestAuthJson<AuthUser[]>('/auth/users', {
    signal,
  });
}
